import { DatePicker, Form, Select } from 'antd';
import { FormInstance } from 'antd/es/form/hooks/useForm';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'translations/hooks';

import { Quality_SegmentHistoryStatusEnum_Enum } from 'generated/graphql';

import {
  AddManualScanFormValues,
  FormSelectOption,
  ReceiptStatuses,
  TunnelStatuses,
} from './types';

export interface ZoneOption {
  label: string;
  value: string;
  isReceipt: boolean;
}

const getAvailableStatuses = (zone?: ZoneOption) => {
  if (!zone) {
    return [];
  }

  return zone.isReceipt ? ReceiptStatuses : TunnelStatuses;
};

export const AddManualScanForm = ({
  form,
  save,
  zoneOptions,
  ringOptions,
  loading,
}: {
  form: FormInstance<AddManualScanFormValues>;
  save: (row: AddManualScanFormValues) => void;
  zoneOptions: ZoneOption[];
  ringOptions: FormSelectOption[];
  loading: boolean;
}) => {
  const { t } = useTranslation();

  const selectedZone = Form.useWatch('zone', form);
  const zone = zoneOptions.find((z) => z.value === selectedZone);

  const selectedStatus = Form.useWatch('status', form);
  const isSegmentInstalled =
    selectedStatus === Quality_SegmentHistoryStatusEnum_Enum.Installed;

  const statuses = getAvailableStatuses(zone).map((status) => ({
    value: status,
    label: t(`qualityControl.segmentHistory.status.state`, {
      context: status,
    }),
    disabled: status === Quality_SegmentHistoryStatusEnum_Enum.Quarantine,
  }));

  const disabledDate = (current: Dayjs) => {
    return current && current > dayjs().tz().endOf('day');
  };

  const initialValues = { date: dayjs().tz() };

  return (
    <Form<AddManualScanFormValues>
      form={form}
      layout="vertical"
      onFinish={save}
      initialValues={initialValues}
    >
      <Form.Item<AddManualScanFormValues>
        name="date"
        label={t('qualityControl.segmentHistory.editModal.date')}
        rules={[{ required: true }]}
      >
        <DatePicker
          style={{ width: '100%' }}
          showTime={{ format: 'HH:mm' }}
          format="L HH:mm"
          allowClear={false}
          disabledDate={disabledDate}
        />
      </Form.Item>
      <Form.Item<AddManualScanFormValues>
        name="zone"
        label={t('qualityControl.segmentHistory.drawer.details.zone')}
        rules={[{ required: true }]}
      >
        <Select options={zoneOptions} allowClear={false} loading={loading} />
      </Form.Item>
      <Form.Item<AddManualScanFormValues>
        name="status"
        label={t('qualityControl.segmentHistory.editModal.status')}
        rules={[{ required: true }]}
      >
        <Select options={statuses} allowClear={false} />
      </Form.Item>
      {isSegmentInstalled ? (
        <Form.Item<AddManualScanFormValues>
          name="ringId"
          label={t('qualityControl.segmentHistory.editModal.ringNumber')}
          layout="vertical"
          rules={[
            {
              required: isSegmentInstalled,
            },
          ]}
        >
          <Select
            placeholder={t('input.serialNumber.placeholder')}
            showSearch
            optionFilterProp="label"
            options={ringOptions}
            loading={loading}
          />
        </Form.Item>
      ) : null}
    </Form>
  );
};
